import { useQuery } from "@tanstack/react-query";
import { Search, ShieldCheck, UserCog } from "lucide-react";
import { useState } from "react";
import { fetchAuditLogs } from "@/lib/queries";
import { ago } from "@/lib/format";

export function AuditPage() {
  const [search, setSearch] = useState("");

  const auditLogs = useQuery({
    queryKey: ["audit", search],
    queryFn: () => fetchAuditLogs(),
    placeholderData: (previous) => previous,
  });

  const filteredLogs = auditLogs.data?.filter(
    (log) =>
      log.action.toLowerCase().includes(search.toLowerCase()) ||
      log.target_type?.toLowerCase().includes(search.toLowerCase()) ||
      (log.target_id?.length ?? 0) > 0
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Privileged action audit</h2>
          <p>Every sensitive console action is logged here.</p>
        </div>
        <span className="badge badge-grape">
          <ShieldCheck size={12} />
          audit_log table
        </span>
      </div>

      <div className="search" style={{ marginTop: 24, maxWidth: 480 }}>
        <Search size={14} />
        <input
          className="input"
          placeholder="Search action, target type or id…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <section className="card" style={{ marginTop: 24 }}>
        <div className="card-head">
          <div>
            <h2>Audit log</h2>
            <p>Privilege changes, moderation, and configuration updates</p>
          </div>
          <div className="spacer" />
          <span className="badge">
            <UserCog size={12} style={{ marginRight: 4 }} />
            {auditLogs.data?.length ?? 0} records
          </span>
        </div>

        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Staff</th>
                <th>Action</th>
                <th>Target type</th>
                <th>Target ID</th>
                <th>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.isLoading && (
                <tr>
                  <td colSpan={6} className="empty">
                    <div className="spinner" />
                  </td>
                </tr>
              )}
              {!auditLogs.isLoading && filteredLogs?.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty">
                    {search ? `No audit records matching "${search}"”` : "No audit records yet."}
                  </td>
                </tr>
              )}
              {filteredLogs?.map((log) => (
                <tr key={log.id}>
                  <td className="nowrap faint">{ago(log.created_at)}</td>
                  <td>
                    {log.admin_user_id ? (
                      <div className="row">
                        <div className="avatar avatar-mint" style={{ width: 28, height: 28, fontSize: 12 }}>
                          {(log as any).profiles?.display_name?.slice(0, 1).toUpperCase() ?? "S"}
                        </div>
                        <div>
                          <div className="cell-strong" style={{ fontSize: 12 }}>{(log as any).profiles?.display_name ?? "staff"}</div>
                          <div className="cell-sub mono faint">{log.admin_user_id.slice(0, 8)}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="muted">service role</span>
                    )}
                  </td>
                  <td>
                    <span className="badge badge-sunshine" style={{ fontFamily: "monospace", fontSize: 11 }}>
                      {log.action}
                    </span>
                  </td>
                  <td className="muted">{log.target_type ?? "—"}</td>
                  <td className="mono faint" style={{ fontSize: 11 }}>{log.target_id ?? "—"}</td>
                  <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {log.metadata && Object.keys(log.metadata).length > 0 ? (
                      <pre style={{ fontSize: 11, margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(log.metadata, null, 2).slice(0, 80)}...</pre>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}